import { useEffect, useState } from "react";
import { arTargetKey,
  youtubeSay,
  arPosterKey,
  arSceneReady,
  sceneHas3d,
  cardVideoUrl,
} from "@greg-chick/shared";
import {
  absArtUrl,
  contentBase,
  loadManifest,
  publishAssets,
  type Manifest,
  type ManifestEntry,
} from "../lib/contentApi";
import { useRegisterSave } from "../lib/saveBar";
import { useRepoAssets } from "../lib/repoAssets";
import { ArOverlayEditor } from "./ArOverlayEditor";
import { ArMagicEditor } from "./ArMagicEditor";
import { ArTargetPicker } from "./ArTargetPicker";

/**
 * 📱 **AR 카드** — 경주 유적 카드 6장.
 *
 * ## 왜 이 화면이 생겼나
 * AR 은 두 갈래로 자랐다: **책 페이지 AR**(전자책 화면에서 편집)과 **경주 유적 카드 AR**.
 * 그런데 카드 쪽은 **관리자에서 닿을 곳이 아예 없었다** — 아이 화면에는 6장이 멀쩡히 나가는데
 * (게시본에 오버레이·포스터까지 있다), 운영자는 그 무엇도 고칠 수 없었다.
 * 특히 「영상으로 보기」 주소가 6장 모두 `REPLACE` 인 채였다.
 *
 * ## 「영상으로 보기」가 왜 중요한가
 * AR 은 **반드시 실패한다** — 구형 폰, 카메라 거부, 어두운 곳, 카드가 없는 아이.
 * 그때 마지막으로 남는 게 이 영상이다(FR-AR-04). 비어 있으면 아이는 안내 글만 보고 끝난다.
 * (자리표시자 `REPLACE` 는 웹이 없는 것으로 걸러서, 눌리는데 아무 일 없는 버튼은 안 생긴다.)
 *
 * ## 목록은 어디서 오나
 * 장면 목록 자체는 웹의 `content/ar-scenes.json` 이다(코드가 아니라 콘텐츠 주도).
 * 여기서 고치는 값은 **게시 매니페스트**(`ar.target.<장면>` 엔트리)에 실려 웹이 바로 읽는다 —
 * 저장소 파일을 건드리지 않고도 반영된다.
 */
interface Scene {
  id: string;
  title: string;
  desc?: string;
  /** 저장소에 적힌 옛 「영상으로 보기」 칸 — 게시본 칸이 비면 아이 화면이 이걸 쓴다(`cardVideoUrl`) */
  legacyVideo?: unknown;
}

/** 웹이 실제로 읽는 목록을 그대로 읽는다 — 관리자만 아는 목록을 따로 두면 반드시 갈린다. */
async function loadScenes(): Promise<Scene[]> {
  const res = await fetch(`${contentBase()}/content/ar-scenes.json`, { cache: "no-cache" });
  if (!res.ok) return [];
  const raw = (await res.json()) as unknown;
  const list = Array.isArray(raw) ? raw : ((raw as { scenes?: unknown[] }).scenes ?? []);
  return list
    .filter((s): s is Record<string, unknown> => !!s && typeof s === "object")
    .map((s) => ({
      id: String(s.id ?? ""),
      title: String(s.title_ko ?? s.title ?? s.id ?? ""),
      desc: typeof s.desc_ko === "string" ? s.desc_ko : undefined,
      legacyVideo: s.fallback_youtube,
    }))
    .filter((s) => s.id.length > 0);
}

/**
 * 🔴 **여기서 다시 짜지 않는다.** 예전에는 이 파일에 제 판정이 있었는데, 웹이 쓰는 판정과
 * 미묘하게 달랐다 — 재생목록 주소(`?list=…`)나 짧은 아이디를 넣으면 여기는 「괜찮아요」,
 * 아이 화면은 「준비 중」이었다. 운영자는 다 해 놓은 줄 알고 넘어간다.
 * 이제 **아이 화면이 쓰는 그 함수**가 판단한다(`shared/youtube.ts`).
 */
// (별칭을 두지 않는다 — 부르는 곳에서 `youtubeSay` 를 그대로 쓴다)

export function ArCardPanel() {
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [manifest, setManifest] = useState<Manifest>({ version: 0, assets: {} });
  /** 저장소 그림표 — 게시본에 없는 카드의 카메라 그림(.mind)이 여기에만 있을 수 있다(아래 setFallback) */
  const repoAssets = useRepoAssets();
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "empty" | "fail">("loading");

  useEffect(() => {
    let alive = true;
    void Promise.all([loadScenes(), loadManifest()])
      .then(([s, m]) => {
        if (!alive) return;
        setScenes(s);
        setManifest(m);
        setState(s.length ? "ok" : "empty");
      })
      /**
       * 🔴 **못 받아 온 것을 말해 준다.** 예전엔 `catch` 가 없어서, 서버가 죽으면
       * 이 화면이 **「불러오는 중…」에서 영영 멈췄다** — 운영자는 왜 안 뜨는지 알 방법이
       * 없고, 브라우저 콘솔에만 「처리되지 않은 거부」가 조용히 쌓였다.
       * (2026-08-27, 화면 스물둘을 서버 없이 열어 보는 검사가 잡았다.)
       */
      .catch(() => {
        if (alive) setState("fail");
      });
    return () => {
      alive = false;
    };
  }, []);

  const setEntry = (key: string, entry: ManifestEntry | null) => {
    setManifest((m) => {
      const next = { ...m.assets };
      if (entry) next[key] = entry;
      else delete next[key];
      return { ...m, assets: next };
    });
    setDirty((d) => new Set(d).add(key));
  };

  /** 「영상으로 보기」는 **타깃 엔트리에 얹는다** — 책 AR 과 같은 자리(웹이 거기만 읽는다). */
  const setFallback = (id: string, url: string) => {
    const key = arTargetKey(id);
    /**
     * 🔴 **게시본에 이 카드가 없으면 저장소 항목에서 출발한다**(2026-10-21 · 로컬에서 운영자 할 일을 걸어 보다 찾음).
     * 게시본 항목은 저장소(정적) 항목을 **통째로 덮는다**. 예전엔 `{ url: "" }` 에서 출발해, 카메라 그림이
     * 저장소에만 있는 카드에 영상 주소를 넣고 올리면 **카메라 인식이 꺼졌다**(그림 주소가 빈 값으로 덮임) —
     * 지우면 카드가 목록에서 사라졌다. 운영은 6장 모두 게시본에 있어 지금은 안 겪지만, 새 카드를 저장소로 들이면 겪는다.
     */
    const cur = manifest.assets[key] ?? (repoAssets?.[key] as ManifestEntry | undefined) ?? { url: "" };
    setEntry(key, { ...cur, fallbackYoutube: url.trim() || undefined });
  };

  async function publish() {
    const out = await publishAssets(manifest.assets);
    setManifest((m) => ({ ...m, version: out.version }));
    setDirty(new Set());
  }
  useRegisterSave({
    id: "arcard",
    label: "AR 카드",
    dirty: dirty.size,
    save: publish,
    // 💾 바꾼 그림 자리만 담는다 — 그림 파일 자체는 올릴 때 이미 서버에 있다.
    //    지운 자리는 null 로 남긴다(키를 빼면 "안 지운 것"이 된다).
    draft: {
      data: () => ({
        entries: Object.fromEntries([...dirty].map((k) => [k, manifest.assets[k] ?? null])),
        dirty: [...dirty],
      }),
      restore: (d) => {
        const v = d as { entries?: Record<string, ManifestEntry | null>; dirty?: string[] };
        if (!v?.entries) return;
        setManifest((m) => {
          const next = { ...m.assets };
          for (const [k, e] of Object.entries(v.entries!)) {
            if (e) next[k] = e;
            else delete next[k];
          }
          return { ...m, assets: next };
        });
        setDirty(new Set(v.dirty ?? Object.keys(v.entries!)));
      },
    },
  });

  // 🔴 아이 화면(arData)·출시 준비·check:owner 와 같은 판정 — 게시본 칸이 먼저, 없으면 저장소 옛 칸
  const missing = scenes.filter((s) => !cardVideoUrl(manifest.assets[arTargetKey(s.id)], s.legacyVideo)).length;

  /**
   * 🔴 **이 카드가 지금 아이 화면에 보이는가.**
   *
   * 아이 화면은 「볼 것이 있는 것」만 목록에 세운다 — 카메라가 알아볼 그림(.mind)이 있거나,
   * 대신 볼 영상이 있거나. 둘 다 없으면 **목록에 아예 안 뜬다.**
   *
   * 예전엔 관리자에 「영상이 비었어요」만 떴다. 그걸 본 운영자는 「영상만 없고 AR 은 되겠구나」로
   * 읽는다 — 실제로는 카드 그림도 없어서 **그 카드가 통째로 안 보이는** 상태였다.
   * 판정은 아이 화면과 **같은 함수**(`arSceneReady`)를 부른다.
   */
  const shownToKids = (id: string): boolean =>
    arSceneReady({
      id,
      title_ko: "",
      targetSrc: manifest.assets[arTargetKey(id)]?.url,
      fallback_youtube: cardVideoUrl(manifest.assets[arTargetKey(id)], scenes.find((sc) => sc.id === id)?.legacyVideo),
    });
  const shownCount = scenes.filter((sc) => shownToKids(sc.id)).length;

  return (
    <section className="arcard">
      <p className="hint">
        카드에 카메라를 비추면 나오는 것들이에요. <b>AR 은 안 될 때가 있어요</b> — 오래된 폰,
        카메라를 거부했을 때, 어두운 곳, 카드가 없는 아이. 그때 보여줄 영상을 꼭 넣어 주세요.
      </p>

      {state === "loading" ? <p className="hint">불러오는 중…</p> : null}
      {state === "empty" ? (
        <p className="hint">AR 장면 목록이 비어 있어요({contentBase()}/content/ar-scenes.json).</p>
      ) : null}
      {/* 🔴 **못 받아 온 것과 비어 있는 것은 다르다.** 서버가 죽었을 때 「비어 있어요」라고
          하면 운영자는 목록을 새로 만들려 든다 — 사실은 잠깐 못 읽은 것뿐이다. */}
      {state === "fail" ? (
        <p className="hint">
          AR 장면 목록을 못 불러왔어요. 인터넷이나 웹 주소({contentBase()})를 확인하고 다시 열어
          주세요.
        </p>
      ) : null}

      {scenes.length > 0 ? (
        <p className={shownCount === scenes.length ? "arcard__ok" : "arcard__warn"}>
          👀 카드 {scenes.length}장 중 <b>{shownCount}장</b>이 아이 화면에 보여요.
          {shownCount < scenes.length
            ? " 나머지는 카드 그림(.mind)이나 영상이 없어서 목록에 안 떠요."
            : ""}
        </p>
      ) : null}

      {missing > 0 ? (
        <p className="arcard__warn">
          ⚠️ 「영상으로 보기」가 비어 있는 카드가 <b>{missing}장</b> 있어요. 비어 있으면 AR 이 안 될
          때 아이는 안내 글만 보고 끝나요.
        </p>
      ) : null}

      <ul className="arcard__list">
        {scenes.map((s) => {
          const targetKey = arTargetKey(s.id);
          const target = manifest.assets[targetKey];
          const poster = manifest.assets[arPosterKey(s.id)]?.url;
          const yt = target?.fallbackYoutube ?? "";
          const hint = youtubeSay(yt);
          const isOpen = open === s.id;
          return (
            <li key={s.id} className="arcard__item">
              <div className="arcard__head">
                {/* 포스터는 아이 화면 **목록에서 곧 제목**이다(글 못 읽는 아이가 이걸로 고른다).
                    접힌 곳에 있으면 비어 있는 줄도 모른다 — 여기서 바로 보여준다. */}
                {poster ? (
                  <img className="arcard__thumb" src={absArtUrl(poster)} alt="" loading="lazy" />
                ) : (
                  <span className="arcard__thumb arcard__thumb--none" aria-hidden="true">
                    ?
                  </span>
                )}
                <div className="arcard__headtext">
                  <b>{s.title}</b>
                  {/* 🔴 이 카드가 아이 화면에 뜨는지 — 「영상이 비었다」보다 이게 먼저다 */}
                  <span
                    className={shownToKids(s.id) ? "arcard__live" : "arcard__hidden"}
                    title={
                      shownToKids(s.id)
                        ? "아이 화면 목록에 떠요"
                        : "카드 그림(.mind)이나 영상이 있어야 목록에 떠요"
                    }
                  >
                    {shownToKids(s.id) ? "👀 보여요" : "🚫 아직 안 보여요"}
                  </span>
                  {/* 🧊 3D 가 붙은 카드 — 3D 칸은 [그림·움직임·3D] 를 펼쳐야 보여서, 어디에 붙었는지 줄에서 안 보였다(2026-10-03) */}
                  {sceneHas3d(manifest, s.id) ? (
                    <span className="arcard__3d" title="이 카드에 3D 가 붙어 있어요 — [그림·움직임·3D]를 펼치면 ④ 3D 모델 칸이 있어요">
                      🧊 3D
                    </span>
                  ) : null}
                  {s.desc ? <p className="arcard__desc">{s.desc}</p> : null}
                  {!poster ? (
                    <p className="arcard__hint">
                      목록에 보일 그림이 없어요 — [그림·움직임·3D]에서 ③ 포스터를 올려 주세요.
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={() => setOpen(isOpen ? null : s.id)}
                >
                  {isOpen ? "접기" : "그림·움직임·3D ▾"}
                </button>
              </div>

              <label className="arcard__yt">
                <span>영상으로 보기 (유튜브 주소)</span>
                <input
                  type="url"
                  value={yt}
                  placeholder="https://youtu.be/…"
                  onChange={(e) => setFallback(s.id, e.target.value)}
                />
              </label>
              {hint ? <p className="arcard__hint">{hint}</p> : null}

              {isOpen ? (
                <div className="arcard__edit">
                  <ArTargetPicker
                    idKey={s.id}
                    title={s.title}
                    assets={manifest.assets}
                    setEntry={setEntry}
                    // 카드 그림이 곧 카메라가 외울 그림이다 — 다시 올릴 이유가 없다
                    posterUrl={poster ? absArtUrl(poster) : undefined}
                  />
                  <ArOverlayEditor
                    idKey={s.id}
                    title={s.title}
                    assets={manifest.assets}
                    setEntry={setEntry}
                  />
                  {/* 🃏 누르면 일어나는 마법·🧊 3D 층 — 책 쪽과 **같은 편집기**(판정도 같은 shared). 2026-09-13 수막새 카드 3D */}
                  <CardMagicEditor
                    idKey={s.id}
                    title={s.title}
                    posterUrl={poster ? absArtUrl(poster) : undefined}
                    assets={manifest.assets}
                    setEntry={setEntry}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * 🃏 **경주 카드의 마법·3D** — 책 쪽 편집기(`ArMagicEditor`)를 그대로 쓴다. 다른 것은 **자리의 기준 상자**뿐:
 * 카드는 가로(수막새 1280×716)라 쪽(3:4·정사각)과 비율이 다르다. 비율을 틀리게 주면 운영자가 그린 네모가
 * 아이 화면에선 딴 데로 간다(아무 에러 없이) — 그래서 카드 그림을 **재서** 넘긴다. 못 재면 그리지 않는다(틀린 상자보다 낫다).
 */
function CardMagicEditor({
  idKey,
  title,
  posterUrl,
  assets,
  setEntry,
}: {
  idKey: string;
  title: string;
  posterUrl?: string;
  assets: Parameters<typeof ArMagicEditor>[0]["assets"];
  setEntry: Parameters<typeof ArMagicEditor>[0]["setEntry"];
}) {
  const [aspect, setAspect] = useState<string | null>(null);
  useEffect(() => {
    setAspect(null);
    if (!posterUrl) return;
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (alive && img.naturalWidth > 0 && img.naturalHeight > 0) setAspect(`${img.naturalWidth}/${img.naturalHeight}`);
    };
    img.src = posterUrl;
    return () => {
      alive = false;
    };
  }, [posterUrl]);
  if (!posterUrl) {
    return <p className="arcard__hint">🪄 마법·🧊 3D 를 붙이려면 먼저 카드 그림(포스터)을 올려 주세요 — 자리를 그 그림 위에 잡아요.</p>;
  }
  if (!aspect) return <p className="arcard__hint">카드 그림 크기를 재는 중…</p>;
  return <ArMagicEditor idKey={idKey} title={title} pageImageUrl={posterUrl} pageAspect={aspect} assets={assets} setEntry={setEntry} />;
}
